import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import {
  PhxSortChangeEvent,
  PhxSortDirection,
  PhxTableColumnConfig,
  PhxTableHeaderConfig,
} from '../../phx-table.types';
import { PHX_SORT_DIRECTION } from '../phx-table-cell.const';
import { ArrowDownUI, ArrowUpUI } from '../../../icon/icon-map';
import { KeyboardKeys } from '../../../../helpers/KeyboardKeys.enum';

/** `aria-sort` only accepts these tokens — the internal `'asc'`/`'desc'` values are not valid ARIA. */
type PhxTableAriaSort = 'ascending' | 'descending' | 'none';

const ARIA_SORT_BY_DIRECTION: Record<'asc' | 'desc', PhxTableAriaSort> = {
  [PHX_SORT_DIRECTION.ASC]: 'ascending',
  [PHX_SORT_DIRECTION.DESC]: 'descending',
};

interface PhxTableHeaderLabelView {
  config: PhxTableHeaderConfig;
  label: string | undefined;
  sortable: boolean;
  sortKey: string;
  sortActive: boolean;
}

/** Header cell for a single column — labels, tooltip, and sort controls. */
@Component({
  selector: 'th[phx-table-cell-header]',
  templateUrl: './phx-table-cell-header.component.html',
  styleUrls: ['./phx-table-cell-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'phx-table-header-cell',
    '[class.phx-table-align-right]': 'phxColumn.align === "right"',
    '[class.phx-table-align-center]': 'phxColumn.align === "center"',
    '[class.phx-table-group-border-right]': 'phxGroupBorderRight',
    '[attr.aria-sort]': 'columnAriaSort',
  },
})
export class PhxTableCellHeaderComponent implements OnChanges {
  /** Column configuration for this header cell. */
  @Input() phxColumn!: PhxTableColumnConfig;
  /** Resolved header label(s) for this column (supports multi-label headers). */
  @Input() phxHeaderConfigs: PhxTableHeaderConfig[] = [];
  /** When true, draws a right border indicating the last column in a group. */
  @Input() phxGroupBorderRight: boolean = false;
  /** Active sort column key used to style the active header label and sort icon. */
  @Input() phxSortKey: string | undefined;
  /** Sort direction for `phxSortKey`. `null` means unsorted. */
  @Input() phxSortDirection: PhxSortDirection = null;

  /** Emitted when a sortable header label or sort icon is activated. */
  @Output() phxSortChange = new EventEmitter<PhxSortChangeEvent>();

  /** Precomputed per-label view model for template bindings. */
  public headerViews: PhxTableHeaderLabelView[] = [];
  /** Whether any label in this column header is sortable. */
  public columnSortable = false;
  /** Whether the active sort key matches any label in this column. */
  public columnSortActive = false;
  /** Sort icon for the column-level sort control. */
  public columnSortIcon = ArrowUpUI;
  /** `aria-sort` announced on the header cell. `null` for non-sortable columns. */
  public columnAriaSort: PhxTableAriaSort | null = null;

  private readonly _sortAscIcon = ArrowUpUI;
  private readonly _sortDescIcon = ArrowDownUI;

  public ngOnChanges(changes: SimpleChanges): void {
    const { phxHeaderConfigs, phxColumn, phxSortKey, phxSortDirection } = changes;

    if (phxHeaderConfigs || phxColumn) {
      this.rebuildHeaderViews();
    } else if (phxSortKey || phxSortDirection) {
      this.updateSortState();
    }
  }

  public onSortClick(header: PhxTableHeaderConfig): void {
    if (!header.sortable) {
      return;
    }

    const key = this.getEffectiveSortKey(header);
    const direction: PhxSortDirection =
      this.phxSortKey === key && this.phxSortDirection === PHX_SORT_DIRECTION.ASC
        ? PHX_SORT_DIRECTION.DESC
        : PHX_SORT_DIRECTION.ASC;

    this.phxSortChange.emit({ key, direction });
  }

  public onSortArrowClick(): void {
    const activeView = this.headerViews.find(view => view.sortable && view.sortActive);
    const targetView = activeView || this.headerViews.find(view => view.sortable);

    if (!targetView) {
      return;
    }

    const direction: PhxSortDirection =
      activeView && this.phxSortDirection === PHX_SORT_DIRECTION.ASC
        ? PHX_SORT_DIRECTION.DESC
        : PHX_SORT_DIRECTION.ASC;

    this.phxSortChange.emit({ key: targetView.sortKey, direction });
  }

  public onSortKeydown(event: KeyboardEvent, header: PhxTableHeaderConfig): void {
    if (event.code === KeyboardKeys.ENTER || event.code === KeyboardKeys.SPACE) {
      event.preventDefault();
      this.onSortClick(header);
    }
  }

  /** Rebuild label structure when column config or header labels change. */
  private rebuildHeaderViews(): void {
    this.columnSortable = this.phxHeaderConfigs.some(header => header.sortable);
    this.headerViews = this.phxHeaderConfigs.map(header => ({
      config: header,
      label: header.label,
      sortable: !!header.sortable,
      sortKey: this.getEffectiveSortKey(header),
      sortActive: false,
    }));
    this.updateSortState();
  }

  /** Refresh sort styling when the active sort key or direction changes. */
  private updateSortState(): void {
    this.headerViews = this.headerViews.map(view => ({
      ...view,
      sortActive: this.phxSortKey === view.sortKey,
    }));

    this.columnSortActive = this.headerViews.some(view => view.sortActive);
    this.columnSortIcon =
      !this.columnSortActive || this.phxSortDirection === PHX_SORT_DIRECTION.ASC
        ? this._sortAscIcon
        : this._sortDescIcon;
    this.columnAriaSort = this.resolveColumnAriaSort();
  }

  /** `aria-sort` lives on the `th` (the `columnheader`) — the label `span` is a `button`, where it has no meaning. */
  private resolveColumnAriaSort(): PhxTableAriaSort | null {
    if (!this.columnSortable) {
      return null;
    }

    return this.columnSortActive && this.phxSortDirection
      ? ARIA_SORT_BY_DIRECTION[this.phxSortDirection]
      : 'none';
  }

  private getEffectiveSortKey(header: PhxTableHeaderConfig): string {
    return header.sortKey || this.phxColumn.key;
  }
}
